import { Button } from "@repo/ui";
import { Heart, Leaf, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HERO_IMG = "https://images.pexels.com/photos/16385321/pexels-photo-16385321.jpeg";
const GALLERY = [
  "https://images.pexels.com/photos/11489968/pexels-photo-11489968.jpeg",
  "https://images.pexels.com/photos/27356022/pexels-photo-27356022.jpeg",
  "https://images.pexels.com/photos/27547168/pexels-photo-27547168.jpeg",
  "https://images.pexels.com/photos/31959994/pexels-photo-31959994.jpeg",
  "https://images.pexels.com/photos/19526278/pexels-photo-19526278.jpeg",
  "https://images.pexels.com/photos/14567258/pexels-photo-14567258.jpeg",
];

export function Home() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen">
      {/* Header - minimal */}
      <header className="px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3 select-none">
          <img src="/paw.svg" alt="Warm Cats" className="h-8 w-8" />
          <span className="text-xl font-semibold tracking-tight">Warm Cats</span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            className="bg-primary text-primary-foreground hover:opacity-90"
            onClick={() => navigate("/cats")}
          >
            Available Cats
          </Button>
        </div>
      </header>

      {/* Hero: immersive image with minimal text overlay */}
      <section className="px-0">
        <div className="relative w-full max-w-7xl mx-auto rounded-[2rem] overflow-hidden">
          <img
            src={HERO_IMG}
            alt="Ginger cat in warm light"
            className="w-full h-[60vh] md:h-[72vh] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-background/30 to-transparent" />
          <div className="absolute bottom-8 left-6 md:left-12 right-6 md:right-12">
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight max-w-xl">
              Cats raised with sincerity.
            </h1>
            <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-xl">
              Carefully bred, gently socialized, and matched to loving homes.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <Button
                size="lg"
                className="bg-primary text-primary-foreground px-6 py-6 rounded-xl"
                onClick={() => navigate("/cats")}
              >
                Meet our cats
              </Button>
              <Button
                size="lg"
                variant="secondary"
                className="rounded-xl"
                onClick={() => {
                  const el = document.getElementById("care-promise");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Our care promise
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust signals */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 rounded-2xl bg-secondary/60">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-primary" />
              <h3 className="font-semibold">Health & transparency</h3>
            </div>
            <p className="mt-2 text-muted-foreground text-sm">
              Vet-checked, vaccinated, and clearly documented.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-secondary/60">
            <div className="flex items-center gap-3">
              <Heart className="h-6 w-6 text-primary" />
              <h3 className="font-semibold">Raised with affection</h3>
            </div>
            <p className="mt-2 text-muted-foreground text-sm">
              Early socialization focused on calm, confident temperaments.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-secondary/60">
            <div className="flex items-center gap-3">
              <Leaf className="h-6 w-6 text-primary" />
              <h3 className="font-semibold">Thoughtful matching</h3>
            </div>
            <p className="mt-2 text-muted-foreground text-sm">
              We help pair each cat with the right home and support after.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery grid - photo first */}
      <section id="care-promise" className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {GALLERY.map((src) => (
            <div key={src} className="rounded-3xl overflow-hidden">
              <img src={src} alt="Cat" className="w-full h-48 md:h-64 object-cover" />
            </div>
          ))}
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="px-6 md:px-10 pb-10 text-sm text-muted-foreground">
        © {new Date().getFullYear()} Warm Cats — ethical, transparent, and caring.
      </footer>
    </div>
  );
}
